import { useFormik } from 'formik'
import React from 'react'
import { FaLock } from 'react-icons/fa'
import { MdEmail } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { restFrameworkSimpleJwt } from '../api/services/rest_framework_simple_jwt'
import { WrongCredentialsError } from '../api/services/rest_framework_simple_jwt/exceptions.ts'
import Button from '../components/common/Button/Button.tsx'
import Card from '../components/common/Card/Card.tsx'
import Input from '../components/common/forms/Input/Input.tsx'
import { useAuth } from '../context/auth'
import { ERROR_CODES } from '../utils/errorCodes.ts'

const BASE_URL = import.meta.env.VITE_BASE_URL

// Reusable Types
interface LoginValues {
  email: string
  password: string
}

// Constants for Validation and Messages
const validationSchema = Yup.object({
  email: Yup.string()
    .email('Niepoprawny adres e-mail')
    .required('E-mail jest wymagany'),
  password: Yup.string().required('Hasło jest wymagane')
})
const AUTH_ERROR_MESSAGE = 'Nieprawidłowe dane logowania'
const GENERIC_ERROR_MESSAGE =
  'Wystąpił nieoczekiwany błąd. Spróbuj ponownie później.'

interface LoginProps {
  errorsFromPD3: string | null
}

const Login: React.FC<LoginProps> = ({ errorsFromPD3 }) => {
  const navigate = useNavigate()
  const { dispatch } = useAuth()
  const from = localStorage.getItem('redirectAfterLogin')

  const errorPD3 = errorsFromPD3
    ? ERROR_CODES[302][Number(errorsFromPD3)]
      ? ERROR_CODES[302][Number(errorsFromPD3)].descriptions.pl
      : 'Wykryto nieokreślony błąd'
    : null

  const authenticateUser = async (
    values: LoginValues,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    try {
      const authData = await restFrameworkSimpleJwt.login(
        values.email,
        values.password
      )
      dispatch({
        type: 'LOGIN',
        payload: {
          email: values.email,
          accessToken: authData.access,
          refreshToken: authData.refresh,
          groups: authData.groups
        }
      })
      navigate(from ? from : '/', { state: { from }, replace: true })
    } catch (error) {
      const errorMessage =
        error instanceof WrongCredentialsError
          ? AUTH_ERROR_MESSAGE
          : GENERIC_ERROR_MESSAGE
      toast.error(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const formik = useFormik<LoginValues>({
    initialValues: { email: '', password: '' },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      await authenticateUser(values, setSubmitting)
    }
  })

  return (
    <div className="p-5">
      <div className="mx-auto max-w-[500px] mt-11">
        <Card>
          <Card.Body>
            <img
              src={'/dd_mobile_logo.png'}
              alt="Logo"
              className="mb-5 mx-auto"
            />
            <form onSubmit={formik.handleSubmit}>
              <div className="mb-4">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  placeholder="E-mail"
                  formik={formik}
                  icon={<MdEmail />}
                />
              </div>
              <div className="mb-6">
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Hasło"
                  formik={formik}
                  icon={<FaLock />}
                />
              </div>
              <div className="mb-5">
                <Button
                  variant="filled"
                  fullWidth
                  size="medium"
                  color="primary"
                  type="submit"
                  className="mb-3"
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? 'Logowanie...' : 'Zaloguj'}
                </Button>
                <div className="text-center mt-2 mb-5">
                  Pozostałe opcje logowania
                </div>

                <Button
                  variant="outlined"
                  fullWidth
                  size="medium"
                  color="secondary"
                  type="button"
                  className={'py-4'}
                  onClick={() =>
                    window.open(`${BASE_URL}/auth/login/pd3/`, '_self')
                  }
                >
                  <img
                    src={'/pd3_icon.png'}
                    width={20}
                    alt="Logo Profidiet"
                    className="mr-2"
                  />
                  Zaloguj z Profidiet
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  size="medium"
                  color="secondary"
                  type="button"
                  className={'mt-2'}
                  onClick={() =>
                    window.open(`${BASE_URL}/auth/login/cosmo/`, '_self')
                  }
                >
                  <img
                    src={'/dietokosmetologia_icon.png'}
                    width={40}
                    alt="Logo Dietokosmetologia"
                    className="mr-2"
                  />
                  Zaloguj z Dietokosmetologia
                </Button>

                {errorPD3 && (
                  <span className="text-red-500 text-xl">{errorPD3}</span>
                )}
              </div>
            </form>
          </Card.Body>
        </Card>
      </div>
    </div>
  )
}

export default Login
